import { useNavigate } from 'react-router-dom'
import {
  Bell,
  ClipboardList,
  CreditCard,
  FileText,
  Megaphone,
  Receipt,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { useAuth } from '@/hooks/use-auth'
import { useMarkNotificationRead, useNotifications } from '@/hooks/use-notifications'
import { notificationHref } from '@/lib/notifications'
import type { Notification, NotificationType } from '@riverpark/types'

const ICON_BY_TYPE: Record<NotificationType, LucideIcon> = {
  invoice_created: Receipt,
  payment_confirmed: CreditCard,
  payment_failed: CreditCard,
  overdue_reminder: Receipt,
  request_created: ClipboardList,
  request_update: ClipboardList,
  announcement: Megaphone,
  approval_required: ShieldCheck,
  approval_result: ShieldCheck,
  document_requested: FileText,
  document_request_update: FileText,
}

function formatDateTime(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleString('en-KE', {
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** For anyone other than the account owner — there's no endpoint that exposes another member's
 * notification history (and there shouldn't be), so staff viewing someone else's profile keep
 * seeing this placeholder. */
function ActivityUnavailable() {
  return (
    <Card className="border-dashed">
      <CardContent className="flex flex-col items-center py-14 text-center">
        <div className="mb-4 flex size-14 items-center justify-center rounded-full border border-border-2 bg-cream-2">
          <Bell className="size-6 text-muted-foreground" strokeWidth={1.5} />
        </div>
        <p className="font-serif text-xl text-ink">Activity is private</p>
        <p className="mt-2 max-w-md font-sans text-sm leading-relaxed text-muted-foreground">
          A member's activity timeline is only visible on their own account.
        </p>
      </CardContent>
    </Card>
  )
}

export function ProfileActivityPanel({ isSelf }: { isSelf: boolean }) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { data, isLoading } = useNotifications()
  const markOne = useMarkNotificationRead()

  if (!isSelf) return <ActivityUnavailable />

  const items = data?.data ?? []

  function handleClick(item: Notification) {
    if (!item.readAt) markOne.mutate(item.id)
    const href = user ? notificationHref(item, user.role) : null
    if (href) navigate(href)
  }

  if (isLoading) {
    return <p className="font-sans text-sm text-muted-foreground">Loading…</p>
  }

  if (items.length === 0) {
    return (
      <Card className="border-dashed">
        <CardContent className="flex flex-col items-center py-14 text-center">
          <div className="mb-4 flex size-14 items-center justify-center rounded-full border border-border-2 bg-cream-2">
            <Bell className="size-6 text-muted-foreground" strokeWidth={1.5} />
          </div>
          <p className="font-serif text-xl text-ink">Nothing here yet</p>
          <p className="mt-2 max-w-md font-sans text-sm leading-relaxed text-muted-foreground">
            Billing events, payments, approvals, and document requests will show up here as they
            happen.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="rounded-card border border-border-2 bg-card">
      <ul className="divide-y divide-border-2">
        {items.map((item) => {
          const Icon = ICON_BY_TYPE[item.type] ?? Bell
          const href = user ? notificationHref(item, user.role) : null
          return (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => handleClick(item)}
                disabled={!href}
                className="flex w-full items-start gap-3 px-5 py-3.5 text-left transition-colors hover:bg-cream-2 disabled:cursor-default disabled:hover:bg-transparent"
              >
                <div className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full border border-border-2 bg-cream-2">
                  <Icon size={15} className="text-muted-foreground" strokeWidth={1.75} />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-sans text-sm font-medium text-ink">{item.title}</p>
                    {!item.readAt && (
                      <span className="mt-1.5 size-1.5 shrink-0 rounded-full bg-primary" />
                    )}
                  </div>
                  <p className="mt-0.5 font-sans text-xs text-muted-foreground">{item.body}</p>
                  <p className="mt-1 font-sans text-[11px] text-muted-foreground/70">
                    {formatDateTime(item.createdAt)}
                  </p>
                </div>
              </button>
            </li>
          )
        })}
      </ul>
    </div>
  )
}
